<template>
  <div class="document-thumbnail">
    <!-- Show actual thumbnail when NOT processing -->
    <!-- Server-generated thumbnail if available -->
    <template v-if="thumbnailUrl">
      <img v-if="!thumbnailError" :src="thumbnailUrl" :alt="fileName" class="thumbnail-image" @error="handleThumbnailError" @load="handleThumbnailLoad" />
      <!-- Show icon if image failed to load -->
      <div v-else class="icon-fallback">
        <q-icon :name="documentIcon" size="48px" :color="iconColor" />
      </div>
    </template>

    <!-- Fallback to icon when no thumbnail URL -->
    <div v-else class="icon-fallback">
      <q-icon :name="documentIcon" size="48px" :color="iconColor" />
    </div>
  </div>
</template>

<script>
import { getDocumentType } from '@/utils/fileUpload';
import { getDocumentThumbnailUrl } from '@/utils/documentProcessingUtils';

export default {
  name: 'DocumentThumbnail',
  props: {
    content: {
      type: Object,
      required: true
    },
    // Add processing status prop (injected from parent like videos)
    documentProcessingStatuses: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      thumbnailError: false
    };
  },
  computed: {
    fileName() {
      return this.content?.filename || this.content?.name || 'Unknown';
    },

    thumbnailUrl() {
      // Use centralized utility to get thumbnail URL
      const thumbnailUrl = getDocumentThumbnailUrl(this.content, this.documentProcessingStatuses);

      if (thumbnailUrl) {
        return thumbnailUrl;
      }

      // No URLs available
      return null;
    },

    documentType() {
      return getDocumentType(this.content);
    },

    documentIcon() {
      const docType = this.documentType;
      if (docType === 'pdf') return 'picture_as_pdf';
      return 'description';
    },

    iconColor() {
      const docType = this.documentType;
      if (docType === 'pdf') return 'red-5';
      return 'blue-5';
    }
  },
  watch: {
    // Reset thumbnail error when URL changes
    thumbnailUrl(newUrl, oldUrl) {
      if (newUrl && newUrl !== oldUrl) {
        this.thumbnailError = false;
      }
    },
    // Reset thumbnail error when content changes
    'content.id'(newId, oldId) {
      if (newId && newId !== oldId) {
        this.thumbnailError = false;
      }
    }
  },
  mounted() {
    // Reset thumbnail error on mount
    this.thumbnailError = false;
  },
  methods: {
    handleThumbnailError() {
      this.thumbnailError = true;
    },

    handleThumbnailLoad() {
      this.thumbnailError = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.document-thumbnail {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 6px;
  overflow: hidden;
  background: var(--light-grey-hr);
  display: flex;
  align-items: center;
  justify-content: center;
}

.thumbnail-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.icon-fallback {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: var(--light-grey-hr);
}
</style>
